import { FC, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@app/context/AuthContext'

import { BaseModalType, useModal } from '@context/ModalsContext'

import { Checkbox } from '@ui/components/Checkbox'
import { Link } from '@ui/components/Link'
import { Modal } from '@ui/components/Modal'
import { Popover, PopoverCard } from '@ui/components/Popover'

import { getTwoYearsAgo } from '@utils/date.utils'
import { useWindowResize } from '@utils/domUtils'
import { PopupLayerContext } from '@utils/portals'

import { FilterQueryProps } from '@model/FilterFields'

import { Box } from '@shared/components/Box'

import { CheckBoxProps } from '../TableFilter/Chips/components/Range.modal'
import { Calendar } from './Calendar'
import { DateRangeFromTo } from './DateRangeInput/DateRangeFromTo'
import {
  getDateRangeFormatConfig,
  getDateRangeInputEndError,
  getDateRangeInputStartError,
} from './DateRangeInput/utils'
import { RangeOption, RangeOptions } from './RangeOptions'

import styles from './CalendarPopup.module.scss'

type MonthsShown = 1 | 2
const DUAL_CALENDAR_MONTHS = 2

export interface CalendarPopupProps {
  onConfirm: (date: any, checkVal?: string) => void
  onClear?: () => void
  rangeOptions?: RangeOption[]
  startDate?: Date
  endDate?: Date
  maxDate?: Date
  minDate?: Date
  selectsRange?: boolean
  selectedDate?: Date
  showYearDropdown?: boolean
  showMonthYearPicker?: boolean
  showYearPicker?: boolean
  popupClassName?: string
  popupWrapperClassName?: string
  monthsShown?: MonthsShown
  placement?: 'top' | 'bottom'
  label?: string
  checkBox?: CheckBoxProps
  initialValues?: FilterQueryProps
  disableYearMonthMode?: boolean
  showEarlyDateLimitReason?: boolean
  openExportModal?: () => void
}

interface CalendarPopupDesktopContentProps {
  startDate?: Date
  endDate?: Date
  setDate: (date: [Date | null, Date | null]) => void
  rangeOptions?: RangeOption[]
  maxDate?: Date
  minDate?: Date
  monthsShown?: MonthsShown
  selectsRange?: boolean
  showYearDropdown?: boolean
  showMonthYearPicker?: boolean
  showYearPicker?: boolean
  disableYearMonthMode?: boolean
  showEarlyDateLimitReason?: boolean
  openExportModal?: () => void
  setIsPopupOpen: (open: boolean) => void
}

const CalendarPopupDesktopContent: FC<CalendarPopupDesktopContentProps> = (props) => {
  const {
    startDate,
    endDate,
    monthsShown = 1,
    setDate,
    rangeOptions,
    maxDate,
    minDate,
    selectsRange = true,
    showYearDropdown,
    showMonthYearPicker,
    showYearPicker,
    disableYearMonthMode,
    showEarlyDateLimitReason,
    openExportModal,
    setIsPopupOpen,
  } = props

  const calendarModeProps = selectsRange
    ? {
        selectsRange: true as const,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        selected: startDate || undefined,
      }
    : {
        selected: startDate || undefined,
      }

  return (
    <div onMouseDown={(event) => event.preventDefault()}>
      {selectsRange && (
        <RangeOptions
          onRangeSelect={setDate}
          startDate={startDate}
          endDate={endDate}
          options={rangeOptions}
        />
      )}
      <Calendar
        {...calendarModeProps}
        onChange={(date: any) => {
          if (Array.isArray(date)) {
            setDate(date as [Date | null, Date | null])
          } else {
            setDate([date as Date, null])
          }
        }}
        monthsShown={monthsShown}
        maxDate={maxDate}
        minDate={minDate}
        showYearDropdown={showYearDropdown}
        showMonthYearPicker={showMonthYearPicker}
        showYearPicker={showYearPicker}
        disableYearMonthMode={disableYearMonthMode}
        showEarlyDateLimitReason={showEarlyDateLimitReason}
        openExportModal={openExportModal}
        closeCalendar={() => setIsPopupOpen(false)}
      />
    </div>
  )
}

interface CalendarPopupMobileModalProps {
  onConfirm: (date: [Date | null, Date | null], checkValModal?: string) => void
  onClear?: () => void
  startDate?: Date
  endDate?: Date
  maxDate?: Date
  minDate?: Date
  rangeOptions?: RangeOption[]
  selectsRange?: boolean
  showYearDropdown?: boolean
  showMonthYearPicker?: boolean
  showYearPicker?: boolean
  label?: string
  checkBox?: CheckBoxProps
  initialValues?: FilterQueryProps
  monthsShown?: MonthsShown
  disableYearMonthMode?: boolean
  showEarlyDateLimitReason?: boolean
  openExportModal?: () => void
}

const CalendarPopupMobileModal: BaseModalType<CalendarPopupMobileModalProps> = (props) => {
  const { t } = useTranslation()
  const {
    startDate,
    endDate,
    maxDate,
    minDate,
    rangeOptions,
    selectsRange = true,
    showYearDropdown,
    showMonthYearPicker,
    onConfirm,
    onClear,
    showYearPicker,
    close,
    label,
    checkBox,
    initialValues,
    monthsShown,
    disableYearMonthMode,
    showEarlyDateLimitReason,
    openExportModal,
  } = props

  const [auth] = useContext(AuthSessionContext)
  const [internalDate, setInternalDate] = useState<[Date | null, Date | null]>([
    startDate ?? null,
    endDate ?? null,
  ])
  const [internalStart, internalEnd] = internalDate
  const [checkBoxVal, setCheckBoxVal] = useState<string | undefined>(() =>
    checkBox ? (initialValues?.[checkBox.key] as string | undefined) : undefined
  )

  const dateFormat = auth?.dateFormatType?.name
  const config = getDateRangeFormatConfig(dateFormat)

  const effectiveMinDate =
    monthsShown === DUAL_CALENDAR_MONTHS && selectsRange ? minDate ?? getTwoYearsAgo() : minDate

  const [rangeInputStartDate, setRangeInputStartDate] = useState(
    internalStart ? config.format(internalStart) : ''
  )
  const [rangeInputEndDate, setRangeInputEndDate] = useState(
    internalEnd ? config.format(internalEnd) : ''
  )

  useEffect(() => {
    setRangeInputStartDate(internalStart ? config.format(internalStart) : '')
    setRangeInputEndDate(internalEnd ? config.format(internalEnd) : '')
  }, [config, internalStart, internalEnd])

  const isCheckedInternal = !!checkBox && checkBoxVal === checkBox.checkedValue

  const isStartDateRangeInvalid = !!getDateRangeInputStartError({
    value: rangeInputStartDate,
    config,
    minDate: effectiveMinDate,
    maxDate,
    t,
  })
  const isEndDateRangeInvalid = !!getDateRangeInputEndError({
    value: rangeInputEndDate,
    config,
    startDate: internalStart ?? undefined,
    minDate: effectiveMinDate,
    maxDate,
    t,
  })
  const isApplyDisabled = !internalStart || isStartDateRangeInvalid || isEndDateRangeInvalid

  const updateFromTuple = (tuple: [Date | null, Date | null]) => {
    setInternalDate(tuple)
  }

  const handleRangeSelect = (tuple: [Date | null, Date | null]) => {
    const [start, end] = tuple
    updateFromTuple(tuple)
    setRangeInputStartDate(start ? config.format(start) : '')
    setRangeInputEndDate(end ? config.format(end) : '')
  }

  const handleCalendarChange = (date: any) => {
    if (Array.isArray(date)) {
      const [start, end] = date as [Date | null, Date | null]
      updateFromTuple([start, end])
      setRangeInputStartDate(start ? config.format(start) : '')
      setRangeInputEndDate(end ? config.format(end) : '')
    } else {
      const single = date as Date | null
      updateFromTuple([single, null])
      setRangeInputStartDate(single ? config.format(single) : '')
      setRangeInputEndDate('')
    }
  }

  const toggleCheckbox = () => {
    if (!checkBox) return
    const next = isCheckedInternal ? checkBox.uncheckedValue : checkBox.checkedValue
    setCheckBoxVal(next)
  }

  const calendarModeProps = selectsRange
    ? {
        selectsRange: true as const,
        startDate: internalStart || undefined,
        endDate: internalEnd || undefined,
        selected: internalStart || undefined,
      }
    : {
        selected: internalStart || undefined,
      }

  const handleApply = () => {
    onConfirm(internalDate, checkBoxVal)
    close()
  }

  const handleClear = () => {
    setInternalDate([null, null])
    setRangeInputStartDate('')
    setRangeInputEndDate('')
    setCheckBoxVal(checkBox?.uncheckedValue)
    onClear?.()
  }

  return (
    <Modal.FullScreen>
      <Modal.Header canClose={false}>
        <Box flex justify='space-between' align='center'>
          {label ??
            (selectsRange && monthsShown === DUAL_CALENDAR_MONTHS
              ? t('Calendar.Choose period')
              : t('Calendar.Choose Date'))}
          <Link size='xl' variant='secondary' underline onClick={handleClear}>
            {t('Clear')}
          </Link>
        </Box>
      </Modal.Header>
      <Modal.Body>
        <Box className={styles.calendarPopupMobileContent}>
          {selectsRange && (
            <RangeOptions
              onRangeSelect={handleRangeSelect}
              startDate={internalStart || undefined}
              endDate={internalEnd || undefined}
              options={rangeOptions}
            />
          )}

          {monthsShown === DUAL_CALENDAR_MONTHS && selectsRange && (
            <Box px='x16' py='x8' mt='x8' direction='column'>
              <DateRangeFromTo
                startValue={rangeInputStartDate}
                endValue={rangeInputEndDate}
                startDate={internalStart ?? undefined}
                endDate={internalEnd ?? undefined}
                dateFormat={dateFormat}
                minDate={effectiveMinDate}
                maxDate={maxDate}
                onStartChange={(value, date) => {
                  setRangeInputStartDate(value)
                  const error = getDateRangeInputStartError({
                    value,
                    config,
                    minDate: effectiveMinDate,
                    maxDate: maxDate ?? new Date(),
                    t,
                  })
                  if (date && error === undefined) updateFromTuple([date, internalEnd])
                }}
                onEndChange={(value, date) => {
                  setRangeInputEndDate(value)
                  const error = getDateRangeInputEndError({
                    value,
                    config,
                    startDate: internalStart ?? undefined,
                    minDate: effectiveMinDate,
                    maxDate: maxDate ?? new Date(),
                    t,
                  })
                  if (date && error === undefined) updateFromTuple([internalStart, date])
                }}
              />
            </Box>
          )}

          <Calendar
            {...calendarModeProps}
            onChange={handleCalendarChange}
            monthsShown={1}
            maxDate={maxDate}
            minDate={effectiveMinDate}
            showYearDropdown={showYearDropdown}
            showMonthYearPicker={showMonthYearPicker}
            showYearPicker={showYearPicker}
            disableYearMonthMode={disableYearMonthMode}
            showEarlyDateLimitReason={showEarlyDateLimitReason}
            openExportModal={openExportModal}
            closeCalendar={close}
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Top>
          {checkBox && (
            <Box pt='x20'>
              <Checkbox checked={isCheckedInternal} onChange={toggleCheckbox}>
                {checkBox.label}
              </Checkbox>
            </Box>
          )}
        </Modal.Footer.Top>
        <Modal.Footer.Secondary onClick={() => close()}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary onClick={handleApply} disabled={isApplyDisabled}>
          {t('Apply')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal.FullScreen>
  )
}

export const CalendarPopup: FC<PropsWithChildren<CalendarPopupProps>> = (props) => {
  const {
    onConfirm,
    onClear,
    children,
    monthsShown,
    rangeOptions,
    popupClassName,
    popupWrapperClassName,
    minDate,
    maxDate,
    label,
    selectsRange = true,
    selectedDate,
    showYearDropdown,
    showMonthYearPicker,
    showYearPicker,
    placement = 'top',
    disableYearMonthMode,
    checkBox,
    initialValues,
    showEarlyDateLimitReason,
    openExportModal,
  } = props

  const isMobile = useWindowResize()

  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [date, setDate] = useState<[Date | null, Date | null]>([null, null])

  const [startDate, endDate] = date

  const initialCheckBoxValue = checkBox
    ? (initialValues?.[checkBox.key] as string | undefined)
    : undefined

  const handleConfirm = useCallback(
    (newDate?: [Date | null, Date | null], checkBoxValModal?: string) => {
      const [start, end] = newDate ?? date

      // if only start is selected, use it as both start and end
      const safeDate: [Date | null, Date | null] = [start, end ?? start]

      setDate(safeDate)

      if (selectsRange) {
        onConfirm(safeDate, checkBoxValModal ?? initialCheckBoxValue)
      } else {
        const single = start ?? selectedDate ?? null
        if (single) {
          onConfirm(single, checkBoxValModal ?? initialCheckBoxValue)
        } else {
          onConfirm(null)
        }
      }

      setIsPopupOpen(false)
    },
    [date, initialCheckBoxValue, onConfirm, selectedDate, selectsRange]
  )

  const handlePopupToggle = useCallback(
    (nextIsOpen: boolean) => {
      if (nextIsOpen) {
        setIsPopupOpen(true)
        return
      }

      if (!isPopupOpen) return

      if (date[0]) {
        handleConfirm()
        return
      }

      setIsPopupOpen(false)
    },
    [date, handleConfirm, isPopupOpen]
  )

  const handleDateSet = (nextDate: [Date | null, Date | null]) => {
    setDate(nextDate)

    if (selectsRange) {
      if (nextDate[0] && nextDate[1]) {
        handleConfirm(nextDate)
      }
      return
    }

    if (nextDate[0]) {
      handleConfirm([nextDate[0], null])
    }
  }

  useEffect(() => {
    if (selectsRange) {
      setDate([props.startDate ?? null, props.endDate ?? null])
    } else {
      setDate([selectedDate ?? null, null])
    }
  }, [props.startDate, props.endDate, selectsRange, selectedDate])

  const mobileModal = useModal(CalendarPopupMobileModal)

  if (isMobile) {
    return (
      <div
        onClick={() =>
          mobileModal.open({
            onConfirm: (newDate, checkValModal) => handleConfirm(newDate, checkValModal),
            onClear,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            maxDate,
            label,
            minDate,
            rangeOptions,
            selectsRange,
            showYearDropdown,
            showMonthYearPicker,
            showYearPicker,
            checkBox,
            initialValues,
            monthsShown,
            disableYearMonthMode,
            showEarlyDateLimitReason,
            openExportModal,
          })
        }
      >
        {children}
      </div>
    )
  }

  return (
    <div
      className={popupWrapperClassName}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
    >
      <PopupLayerContext.Provider value={true}>
        <Popover
          direction={placement === 'bottom' ? 'topLeft' : 'bottomLeft'}
          isOpen={isPopupOpen}
          setIsOpen={handlePopupToggle}
          contentClassName={popupClassName}
          content={
            <PopoverCard>
              <CalendarPopupDesktopContent
                rangeOptions={rangeOptions}
                monthsShown={monthsShown}
                setDate={handleDateSet}
                endDate={endDate || undefined}
                startDate={startDate || undefined}
                maxDate={maxDate}
                minDate={minDate}
                selectsRange={selectsRange}
                showYearDropdown={showYearDropdown}
                showMonthYearPicker={showMonthYearPicker}
                showYearPicker={showYearPicker}
                disableYearMonthMode={disableYearMonthMode}
                showEarlyDateLimitReason={showEarlyDateLimitReason}
                openExportModal={openExportModal}
                setIsPopupOpen={setIsPopupOpen}
              />
            </PopoverCard>
          }
        >
          {children}
        </Popover>
      </PopupLayerContext.Provider>
    </div>
  )
}
